/**
 * Client-side Basecamp project relevance ranking for Projects list "Suggested".
 * Signals (v1): Maater open/touch, bookmarks on todos in the project, lastSync.
 * Cap ~20 for Suggested mode; caller can reveal the rest.
 */

import type { BasecampProject } from '@/types/basecamp'

export const PROJECTS_RELEVANCE_CAP = 20
export const PROJECT_TOUCH_KEY = 'flownotes-bc-project-touched-v1'
export const PROJECTS_SORT_KEY = 'flownotes-bc-projects-sort-v1'

export const PROJECTS_SORT_MODES = [
  'suggested',
  'bookmarks',
  'recent',
  'synced',
  'az',
] as const

export type ProjectsSortMode = (typeof PROJECTS_SORT_MODES)[number]

export const PROJECTS_SORT_LABELS: Record<ProjectsSortMode, string> = {
  suggested: 'Suggested',
  bookmarks: 'Bookmarks',
  recent: 'Recently opened',
  synced: 'Last synced',
  az: 'Name A-Z',
}

export function isProjectsSortMode(value: string | null): value is ProjectsSortMode {
  return (
    value != null &&
    (PROJECTS_SORT_MODES as readonly string[]).includes(value)
  )
}

type TouchMap = Record<string, string>

export function loadProjectTouches(): TouchMap {
  try {
    const raw = localStorage.getItem(PROJECT_TOUCH_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as TouchMap
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export function recordProjectTouch(projectId: string | number): void {
  const id = String(projectId)
  if (!id) return
  try {
    const map = loadProjectTouches()
    map[id] = new Date().toISOString()
    localStorage.setItem(PROJECT_TOUCH_KEY, JSON.stringify(map))
  } catch {
    /* ignore quota */
  }
}

const PROJECTS_UI_STATE_KEY_PREFIX = 'flownotes-basecamp-projects-ui:'
export const PROJECTS_PREVIEW_OPEN_KEY = 'flownotes-projects-preview-open'

export type ProjectsPanelUiState = {
  selectedId: string | null
  previewOpen: boolean
}

function projectsUiStateKey(organizationId?: string): string {
  return `${PROJECTS_UI_STATE_KEY_PREFIX}${organizationId ?? 'default'}`
}

export function loadProjectsPanelUiState(
  organizationId?: string,
): ProjectsPanelUiState {
  try {
    const raw = sessionStorage.getItem(projectsUiStateKey(organizationId))
    if (raw) {
      const parsed = JSON.parse(raw) as ProjectsPanelUiState
      if (parsed && typeof parsed === 'object') {
        return {
          selectedId:
            typeof parsed.selectedId === 'string' ? parsed.selectedId : null,
          previewOpen: Boolean(parsed.previewOpen),
        }
      }
    }
  } catch {
    /* ignore */
  }
  try {
    return {
      selectedId: null,
      previewOpen: localStorage.getItem(PROJECTS_PREVIEW_OPEN_KEY) === 'true',
    }
  } catch {
    return { selectedId: null, previewOpen: false }
  }
}

export function saveProjectsPanelUiState(
  organizationId: string | undefined,
  state: ProjectsPanelUiState,
): void {
  try {
    sessionStorage.setItem(
      projectsUiStateKey(organizationId),
      JSON.stringify(state),
    )
  } catch {
    /* ignore */
  }
  try {
    localStorage.setItem(
      PROJECTS_PREVIEW_OPEN_KEY,
      state.previewOpen ? 'true' : 'false',
    )
  } catch {
    /* ignore */
  }
}

/** True when a restored selection is no longer in the loaded project list. */
export function shouldClearStaleProjectsSelection(opts: {
  selectedId: string | null
  projectsReady: boolean
  validIds: Iterable<string>
}): boolean {
  if (!opts.selectedId || !opts.projectsReady) return false
  const ids = opts.validIds instanceof Set ? opts.validIds : new Set(opts.validIds)
  if (ids.size === 0) return true
  return !ids.has(opts.selectedId)
}

/** Call before leaving Projects for a project/todo so Back restores list + preview. */
export function rememberProjectsPanelSelection(
  projectId: string | number | null | undefined,
  organizationId?: string,
): void {
  const id = projectId != null ? String(projectId) : null
  if (!id) return
  saveProjectsPanelUiState(organizationId, {
    selectedId: id,
    previewOpen: true,
  })
}

export function loadProjectsSortMode(): ProjectsSortMode {
  try {
    const v = localStorage.getItem(PROJECTS_SORT_KEY)
    return isProjectsSortMode(v) ? v : 'suggested'
  } catch {
    return 'suggested'
  }
}

export function saveProjectsSortMode(mode: ProjectsSortMode): void {
  try {
    localStorage.setItem(PROJECTS_SORT_KEY, mode)
  } catch {
    /* ignore */
  }
}

const DAY_MS = 86_400_000

export function scoreProjectRelevance(opts: {
  projectId: string
  bookmarkCount: number
  touchedAtIso?: string | null
  lastSync?: Date | string | null
  pinned?: boolean
}): number {
  const { bookmarkCount, touchedAtIso, lastSync, pinned } = opts
  if (pinned) return 1e15 + bookmarkCount

  let score = 0
  if (touchedAtIso) {
    const t = new Date(touchedAtIso).getTime()
    if (!Number.isNaN(t)) score += t
  }
  // Each bookmarked todo in this project ~= 2 days of recency weight
  score += bookmarkCount * DAY_MS * 2

  if (lastSync) {
    const syncMs = new Date(lastSync).getTime()
    if (!Number.isNaN(syncMs)) {
      // Mild boost so recently-synced projects beat never-touched ones
      score += syncMs * 0.15
    }
  }

  return score
}

export type RankedProject = {
  project: BasecampProject
  id: string
  score: number
  bookmarkCount: number
}

export function rankProjectsByRelevance(
  projects: BasecampProject[],
  opts: {
    bookmarkCounts: Record<string, number>
    touches: TouchMap
    pinnedIds?: Set<string>
  },
): RankedProject[] {
  const { bookmarkCounts, touches, pinnedIds } = opts
  return projects
    .filter((p): p is BasecampProject => p != null && p.id != null)
    .map((project) => {
      const id = String(project.id)
      const bookmarkCount = bookmarkCounts[id] ?? 0
      const score = scoreProjectRelevance({
        projectId: id,
        bookmarkCount,
        touchedAtIso: touches[id],
        lastSync: project.lastSync,
        pinned: pinnedIds?.has(id),
      })
      return { project, id, score, bookmarkCount }
    })
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      return compareProjectName(a, b)
    })
}

function compareProjectName(a: RankedProject, b: RankedProject): number {
  return (a.project.name || '').localeCompare(b.project.name || '')
}

function timestampMs(value?: string | Date | null): number {
  if (!value) return 0
  const n = new Date(value).getTime()
  return Number.isNaN(n) ? 0 : n
}

/** Order the project list for a chosen sort. Suggested keeps the relevance cap in the caller. */
export function orderProjects(
  projects: BasecampProject[],
  mode: ProjectsSortMode,
  opts: {
    bookmarkCounts: Record<string, number>
    touches: TouchMap
    pinnedIds?: Set<string>
  },
): RankedProject[] {
  const rows = rankProjectsByRelevance(projects, opts)
  switch (mode) {
    case 'suggested':
      return rows
    case 'az':
      return [...rows].sort(compareProjectName)
    case 'bookmarks':
      return [...rows].sort(
        (a, b) => b.bookmarkCount - a.bookmarkCount || compareProjectName(a, b),
      )
    case 'recent':
      return [...rows].sort(
        (a, b) =>
          timestampMs(opts.touches[b.id]) - timestampMs(opts.touches[a.id]) ||
          compareProjectName(a, b),
      )
    case 'synced':
      return [...rows].sort(
        (a, b) =>
          timestampMs(b.project.lastSync) - timestampMs(a.project.lastSync) ||
          compareProjectName(a, b),
      )
  }
}

export function formatRelevanceHint(row: RankedProject): string | null {
  const parts: string[] = []
  if (row.bookmarkCount > 0) {
    parts.push(
      `${row.bookmarkCount} bookmark${row.bookmarkCount === 1 ? '' : 's'}`,
    )
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

export const PROJECT_PREVIEW_LOADED_KEY = 'flownotes-bc-project-preview-loaded-v1'

export type ProjectCacheDepth = 'ok' | 'partial' | 'none'

/**
 * List "Ok" means the preview pane can render lists without a Load CTA.
 * Prefer server `cacheDepth` / `listsFresh` from GET /api/basecamp/projects.
 * A leftover previewLoaded timestamp is not enough.
 */
export function resolveProjectCacheDepth(opts: {
  cacheDepth?: ProjectCacheDepth | null
  listsFresh?: boolean
  listsCachedAt?: string | Date | null
  /** @deprecated localStorage SoT removed - ignored when cacheDepth/listsFresh set */
  hasTodoListsCache?: boolean
  lastSync?: Date | string | null
}): ProjectCacheDepth {
  if (opts.cacheDepth === 'ok' || opts.cacheDepth === 'partial' || opts.cacheDepth === 'none') {
    return opts.cacheDepth
  }
  if (opts.listsFresh || opts.hasTodoListsCache) return 'ok'
  if (opts.listsCachedAt || opts.lastSync) return 'partial'
  return 'none'
}

/** Drop preview-loaded timestamps whose todo-list cache is gone (TTL / quota). */
export function reconcilePreviewLoadedMap(
  map: Record<string, string>,
  hasTodoListsCache: (projectId: string) => boolean,
): Record<string, string> {
  const next: Record<string, string> = {}
  for (const [id, iso] of Object.entries(map)) {
    if (id && typeof iso === 'string' && hasTodoListsCache(id)) {
      next[id] = iso
    }
  }
  return next
}

export function loadProjectPreviewLoadedMap(): TouchMap {
  try {
    const raw = localStorage.getItem(PROJECT_PREVIEW_LOADED_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as TouchMap
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

/** Record when preview content for a project finished loading (lists/activity). */
export function recordProjectPreviewLoaded(projectId: string | number): void {
  const id = String(projectId)
  if (!id) return
  try {
    const map = loadProjectPreviewLoadedMap()
    map[id] = new Date().toISOString()
    localStorage.setItem(PROJECT_PREVIEW_LOADED_KEY, JSON.stringify(map))
  } catch {
    /* ignore quota */
  }
}

export function saveProjectPreviewLoadedMap(map: Record<string, string>): void {
  try {
    localStorage.setItem(PROJECT_PREVIEW_LOADED_KEY, JSON.stringify(map))
  } catch {
    /* ignore quota */
  }
}

export function formatRelativeSyncLabel(isoOrDate?: string | Date | null): string {
  if (!isoOrDate) return 'Never synced'
  const ms = new Date(isoOrDate).getTime()
  if (Number.isNaN(ms)) return 'Never synced'
  const diff = Date.now() - ms
  const h = Math.floor(diff / 3_600_000)
  if (diff < 45_000) return 'Just now'
  if (h < 1) return `${Math.max(1, Math.floor(diff / 60_000))}m ago`
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}
